import { ClientPaths } from "newui/utils/navigation/ClientPaths";
import { RfxForbiddenReason } from "../types/rfx-list.type";

/**
 * Server-authoritative 403 reason extraction (TASK-014, AC3; made TOTAL for
 * VT-043; `insufficient_role` named explicitly for VT-043's
 * procurer-detail-block leg — TASK-029 item 7). Mirrors
 * `EvaluationAccessBoundary`'s `getAxios403Reason` (Evaluation-scoped) but is
 * the shared, general-purpose version for any procurer-only surface a panel
 * member (or a company user without the required role) can land on
 * (edit-rfx, view-rfx, etc.) — never derive the reason from FE
 * state/heuristics, only from `response.data.reason`.
 *
 * Returns `undefined` ONLY when `error` isn't a 403 at all (some other
 * status, network error, etc. — those are handled elsewhere, not by this
 * redirect mechanism). Any 403 — whether its `reason` is one of the four
 * known codes, an unrecognized string, or missing entirely — resolves to a
 * real `RfxForbiddenReason` so the caller always has something to redirect
 * on. A bare 403 must never be able to strand the user on a stuck skeleton.
 */
export const extractRfxForbiddenReason = (
	error: unknown
): RfxForbiddenReason | undefined => {
	if (!error || typeof error !== "object") {
		return undefined;
	}

	const err = error as {
		response?: { status?: number; data?: { reason?: string } };
	};

	if (err.response?.status !== 403) {
		return undefined;
	}

	const reason = err.response?.data?.reason;

	return reason === "not_panel_member" ||
		reason === "cross_company" ||
		reason === "revoked" ||
		reason === "insufficient_role"
		? reason
		: "unknown";
};

/**
 * Where a panel member (or role-insufficient company user) should land
 * instead of a bare 403 when they hit a procurer-only URL. All reason codes
 * — including `insufficient_role` and the catch-all `"unknown"` — resolve to
 * the same "permitted view" today (their own Panel list) — kept as an
 * explicit switch (not a constant) so a future reason needing a different
 * target doesn't require hunting down every call site. `default` exists only
 * as a type-narrowing safety net, not as the primary path for unknown
 * reasons — `"unknown"` is handled explicitly above it.
 */
export const getRfxForbiddenRedirectPath = (
	reason: RfxForbiddenReason,
	clientPaths: ClientPaths
): string => {
	switch (reason) {
		case "not_panel_member":
		case "cross_company":
		case "revoked":
		case "insufficient_role":
		case "unknown":
		default:
			return clientPaths.rfx.panel;
	}
};
